import { type HTMLAttributes } from "react";
import { cn } from "../../utils/cn";

export type BadgeTone =
  "neutral" | "brand" | "glacier" | "success" | "error" | "warning" | "info";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Show a leading status dot in the current text color. */
  dot?: boolean;
}

// bg / border-color / text per tone, all token-backed. (border width comes
// from the base `border` class.)
const tones: Record<BadgeTone, string> = {
  neutral: "bg-surface-muted border-subtle text-fg-1",
  brand: "bg-brand-100 border-brand-300 text-brand-800",
  glacier: "bg-glacier border-glacier-border text-tide",
  success: "bg-success-bg border-success-border text-success-fg",
  error: "bg-error-bg border-error-border text-error-fg",
  warning: "bg-warning-bg border-warning-border text-warning-fg",
  info: "bg-info-bg border-info-border text-info-fg",
};

export function Badge({
  tone = "neutral",
  dot = false,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex h-[22px] items-center gap-1 rounded-full border px-2",
        "text-body-sm font-medium [&_svg]:size-4",
        tones[tone],
        className,
      )}
      {...rest}
    >
      {dot && <span className="size-1.5 rounded-full bg-current" />}
      {children}
    </span>
  );
}
